import { useEffect, useMemo, useState } from "react";
import { newAxiosClient } from "@shadowcue/ui";
import type { IPagination } from "@shadowcue/ui";

export interface ITranscription {
  id: number;
  speaker: number;
  text: string;
  createdAt: string;
  scriptTemplateQuestionId?: number | null;
}

interface ITranscriptResponse {
  conversation: { id: number; name: string; scriptTemplateId: number | null };
  transcriptions: ITranscription[];
  pagination: IPagination;
}

interface UseTranscriptResult {
  lines: ITranscription[];
  conversationName: string;
  scriptTemplateId: number | null;
  loading: boolean;
  error: boolean;
}

const useTranscript = (
  id: string | undefined,
  authToken: string,
): UseTranscriptResult => {
  const axios = useMemo(() => newAxiosClient(), []);

  const [lines, setLines] = useState<ITranscription[]>([]);
  const [conversationName, setConversationName] = useState<string>("");
  const [scriptTemplateId, setScriptTemplateId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const headers = { AUTHORIZATION: `Bearer ${authToken}` };

    const fetchAllPages = async () => {
      try {
        const first = await axios.get<ITranscriptResponse>(
          `/api/transcriptions?conversation_id=${id}&limit=50`,
          { headers },
        );
        const { transcriptions, conversation, pagination } = first.data;
        setConversationName(conversation.name);
        setScriptTemplateId(conversation.scriptTemplateId ?? null);

        const paginationPages = pagination?.pages || 1;

        if (paginationPages <= 1) {
          setLines(transcriptions);
          return;
        }

        const pageRequests = Array.from(
          { length: paginationPages - 1 },
          (_, i) =>
            axios.get<ITranscriptResponse>(
              `/api/transcriptions?conversation_id=${id}&limit=50&page=${i + 2}`,
              { headers },
            ),
        );
        const rest = await Promise.all(pageRequests);
        const allLines = [
          ...transcriptions,
          ...rest.flatMap((r) => r.data.transcriptions),
        ];
        setLines(allLines);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchAllPages();
  }, [id]);

  return { lines, conversationName, scriptTemplateId, loading, error };
};

export default useTranscript;
